/**
 * This is a "controlled" DateInput component, made to work with
 * react-hooks-form.
 */
import React from 'react';
import {
  Controller,
  type FieldValues,
  type UseControllerProps,
} from 'react-hook-form';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import isValid from 'date-fns/isValid';
import { BEGINNING_OF_TIME } from '@/lib/constants/dateConstants';
import translate from '@/lib/intl/translate';
import DateInput, { type DateInputProps } from './DateInput';

export const enforceBeginningOfTime = (value: Date) => {
  if (!isValid(value)) {
    return BEGINNING_OF_TIME;
  }
  return isBefore(BEGINNING_OF_TIME, value) ? value : BEGINNING_OF_TIME;
};

export const validateFormat = (value: Date) =>
  !value || value === null
    ? true
    : isValid(value) || translate('INVALID_DATE_FORMAT_MESSAGE');

export const validateMinDate = (minDate: Date) => (value: Date) => {
  if (!isValid(value) || !isValid(minDate)) {
    return true;
  }

  return (
    isBefore(minDate, value) ||
    isEqual(minDate, value) ||
    translate('INVALID_DATE_MIN_MESSAGE', {
      date: format(minDate, 'P'),
    })
  );
};

type ControlledDateInputProps<T extends FieldValues> = Omit<
  DateInputProps,
  'onChange'
> &
  UseControllerProps<T> & {
    control: object;
    defaultValue?: Date;
    error?: boolean;
    helperText?: string;
    minDate?: Date;
    name: string;
    onChange?: (value: Date) => void;
    required?: boolean;
    rules?: object;
    value?: Date;
  };

export default function ControlledDateInput<T extends FieldValues>({
  control,
  defaultValue,
  helperText,
  minDate: minDateProp = BEGINNING_OF_TIME,
  name,
  rules: rulesProp = {},
  ...props
}: ControlledDateInputProps<T>) {
  const minDate = React.useMemo(
    () => enforceBeginningOfTime(minDateProp),
    [minDateProp],
  );
  const rules = React.useMemo(() => {
    const defaultRules = {
      validate: {
        format: validateFormat,
        minDate: validateMinDate(minDate),
      },
    };

    return {
      ...(rulesProp && rulesProp),
      validate: {
        ...(rulesProp?.validate && rulesProp.validate),
        ...defaultRules.validate,
      },
    };
  }, [minDate, rulesProp]);

  return (
    <Controller
      {...(defaultValue && { defaultValue })}
      rules={rules}
      control={control}
      name={name}
      render={({ field: { onBlur, onChange, ref, value } }) => (
        <DateInput
          {...{
            ...props,
            helperText,
            value: value || null,
            minDate,
            onBlur,
            onChange,
            ref,
          }}
        />
      )}
    />
  );
}
